import { AdminGetUserCommand, CognitoIdentityProviderClient } from '@aws-sdk/client-cognito-identity-provider';
import Cookies from 'js-cookie';
import { fetchAuthSession } from 'aws-amplify/auth';
import { format } from 'date-fns';
import * as Sentry from '@sentry/react';

import config from '../../aws-exports';
import { isSensitiveField } from './diff-paths';

/**
 * @param {string} phoneNumber
 * @returns {string | null} E.164 formatted phone number (+11234567890) or null
 */
const tryConvertingPhoneToE164Number = (phoneNumber) => {
  // Remove all non-numeric characters
  let e164PhoneNumber = String(phoneNumber).replace(/\D/g, '');

  if (e164PhoneNumber.length > 10) {
    e164PhoneNumber = e164PhoneNumber.slice(-10);
  }

  if (e164PhoneNumber.length === 10) {
    return `+1${e164PhoneNumber}`;
  }

  return null;
};

export const identify = async (data, trackUrl = true) => {
  if (config.trackingEnabled && window.analytics) {
    if (data && data.phone) {
      data.phone = tryConvertingPhoneToE164Number(data.phone) || data.phone;
    }

    if (data && data.phoneNumber) {
      data.phoneNumber = tryConvertingPhoneToE164Number(data.phoneNumber) || data.phoneNumber;
    }

    // Get cognito user creation date
    let cognitoUserCreationDate = window.localStorage.getItem('cognitoUserCreationDate');
    if (!cognitoUserCreationDate) {
      try {
        const { credentials } = await fetchAuthSession();
        const cognito = new CognitoIdentityProviderClient({
          region: 'us-east-1',
          credentials: {
            accessKeyId: credentials.accessKeyId,
            secretAccessKey: credentials.secretAccessKey,
            sessionToken: credentials.sessionToken
          }
        });

        const cognitoUser = await cognito.send(
          new AdminGetUserCommand({
            Username: data.username,
            UserPoolId: import.meta.env.VITE_STAFF_USER_POOL_ID
          })
        );

        cognitoUserCreationDate = format(new Date(cognitoUser.UserCreateDate), 'yyyy-MM-dd');
        window.localStorage.setItem('cognitoUserCreationDate', cognitoUserCreationDate);
      } catch (e) {
        // Non-fatal (identify() still proceeds without userCreatedDate), but report it: this was
        // one of three catches that made the v3 migration's failures invisible to Sentry (#33733).
        console.error('Error getting cognito user creation date: ', e);
        Sentry.captureException(e, { tags: { flow: 'analytics.identify' }, extra: { username: data.username } });
      }
    }

    data.userCreatedDate = cognitoUserCreationDate;
    const payload = trackUrl ? { ...data, mostRecentClusterUrl: window.location.href.substring(0, 1000) } : data;
    // Identify the user in Segment
    // remove all values where it's longer than 1000 bytes because it will not be accepted by segment
    for (const key of Object.keys(payload)) {
      if (String(payload[key]).length > 1000) {
        payload[key] = '';
      }
    }

    window.analytics.alias(`staff-${data.username}`);
    window.analytics.identify(`staff-${data.username}`, payload);

    // Identify the user in Logrocket
    import('logrocket').then(({ default: LogRocket }) => {
      if (data.username) {
        LogRocket.identify(`staff-${data.username}`, payload);
      }
    });
  }
};

const LOGROCKET_PII_KEYS = new Set([
  'email',
  'phone',
  'phoneNumber',
  'firstName',
  'lastName',
  'fname',
  'lname',
  'address'
]);

const toLogRocketProperties = (data) => {
  if (!data) {
    return undefined;
  }
  const isPrimitive = (value) => ['string', 'number', 'boolean'].includes(typeof value);
  const properties = Object.entries(data).reduce((acc, [key, value]) => {
    if (LOGROCKET_PII_KEYS.has(key) || isSensitiveField(key)) {
      return acc;
    }
    if (isPrimitive(value) || (Array.isArray(value) && value.every(isPrimitive))) {
      acc[key] = value;
    }
    return acc;
  }, {});
  return Object.keys(properties).length > 0 ? properties : undefined;
};

export const track = (event, data) => {
  if (config.trackingEnabled && window.analytics) {
    if (data && data.phone) {
      data.phone = tryConvertingPhoneToE164Number(data.phone) || data.phone;
    }

    if (data && data.phoneNumber) {
      data.phoneNumber = tryConvertingPhoneToE164Number(data.phoneNumber) || data.phoneNumber;
    }

    const branchUsername = window.sessionStorage.getItem('branchSessionUsername') || undefined;
    if (branchUsername) {
      data = { ...data, branchUsername };
    }

    const sessionId = Cookies.get('ampSessionId') || Date.now();
    window.analytics.track(event, data, { integrations: { Amplitude: { sessionId } } });
    const logRocketProperties = toLogRocketProperties(data);
    import('logrocket').then(({ default: LogRocket }) => LogRocket.track(event, logRocketProperties));
    Cookies.set('ampSessionId', sessionId, { expires: 0.5 / 24 }); // session expires after 30 min
  }
};
